import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getLabels } from '../services/api';
import type { LabelsMap, LabelItem } from '../types';

type Lang = 'en' | 'hi';

interface LanguageContextValue {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string, defaultText?: string) => string;
  loadingLabels: boolean;
}

const LanguageContext = createContext<LanguageContextValue>({
  lang: 'en',
  toggleLang: () => {},
  t: (key, defaultText) => defaultText ?? key,
  loadingLabels: false,
});

export const useLanguage = (): LanguageContextValue => useContext(LanguageContext);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [lang, setLang] = useState<Lang>('en');
  const [labels, setLabels] = useState<LabelsMap>({});
  const [loadingLabels, setLoadingLabels] = useState(true);

  useEffect(() => {
    AsyncStorage.getItem('app_lang').then(saved => {
      if (saved === 'hi' || saved === 'en') setLang(saved);
    });
    getLabels()
      .then(res => {
        const map: LabelsMap = {};
        res.data.forEach((item: LabelItem) => { map[item.key] = item.text; });
        setLabels(map);
      })
      .catch(() => {})
      .finally(() => setLoadingLabels(false));
  }, []);

  const toggleLang = useCallback(() => {
    setLang(prev => {
      const next: Lang = prev === 'en' ? 'hi' : 'en';
      AsyncStorage.setItem('app_lang', next);
      return next;
    });
  }, []);

  const t = useCallback(
    (key: string, defaultText?: string): string =>
      labels[key]?.[lang] ?? labels[key]?.en ?? defaultText ?? key,
    [labels, lang],
  );

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t, loadingLabels }}>
      {children}
    </LanguageContext.Provider>
  );
};
